import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { useSWRCache, warmSWRCache, clearSWRCache } from '../services/useSWRCache';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CaretRight, Spinner, FileText, Image as ImageIcon, FilePdf,
  CheckCircle, ShieldCheck, Clock, Warning,
} from '@phosphor-icons/react';
import { MainLayout } from '../components/shared/MainLayout';
import { useUploadAbandonedTracker } from '../hooks/useFrictionEvents';
import UploadDropzone from '../components/documents/UploadDropzone';
import UploadProgressTile from '../components/documents/UploadProgressTile';

/**
 * DocumentsPage — Stage 4 premium rebuild.
 *
 * Shape:
 *   ▸ Header (editorial eyebrow + serif-accented title)
 *   ▸ Trust strip (encryption + ownership signal — 1 line, always visible)
 *   ▸ UploadDropzone (drag-drop or tap; size shrinks once the library has docs)
 *   ▸ In-flight upload tiles (one per file, with live progress / analyzing / ready)
 *   ▸ Category filter pills
 *   ▸ Document list (premium card rows with status chip + type icon)
 *
 * Upload model: we no longer use the old modal sheet. Files dropped on the
 * dropzone are queued immediately — one UploadProgressTile per file — each
 * managing its own XHR upload + background status polling. The user can
 * continue browsing the list while analysis runs.
 */
const DocumentsPage = () => {
  const navigate = useNavigate();
  // Stage-14 perf — stale-while-revalidate cache: returning from a detail page
  // shows the list INSTANTLY from cache while a background refetch keeps it
  // fresh. Fixes the "loads and loads" complaint on back-navigation.
  const { data: documentsData, refetch: refetchDocuments } = useSWRCache('/documents', { ttlMs: 30_000 });
  const documents = documentsData || [];
  const loading = documentsData == null;
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploadQueue, setUploadQueue] = useState([]); // [{id, file, category}]
  const nextTileId = useRef(1);
  const [markUploadOpened, markUploadSubmitted, markUploadClosed] = useUploadAbandonedTracker();

  const categories = [
    { key: 'all',           label: 'All' },
    { key: 'report',        label: 'Reports' },
    { key: 'pathology',     label: 'Pathology' },
    { key: 'imaging',       label: 'Imaging' },
    { key: 'letter',        label: 'Letters' },
    { key: 'prescription',  label: 'Scripts' },
    { key: 'other',         label: 'Other' },
  ];

  // Compatibility shim for downstream code expecting `setDocuments`/`fetchDocuments`.
  const setDocuments = (next) => {
    const value = typeof next === 'function' ? next(documents) : next;
    warmSWRCache('/documents', value);
    refetchDocuments();
  };
  const fetchDocuments = refetchDocuments;

  /** Queue dropped/selected files for upload. */
  const handleFiles = (files) => {
    const accepted = [];
    for (const f of files) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} is larger than 25MB — skipped.`);
        continue;
      }
      accepted.push(f);
    }
    if (!accepted.length) return;
    markUploadOpened();
    markUploadSubmitted();
    const tiles = accepted.map((f) => ({
      id: nextTileId.current++,
      file: f,
      // Heuristic category — backend accepts anything, user can reclassify from detail page later.
      category: guessCategory(f.name),
    }));
    setUploadQueue((q) => [...tiles, ...q]);
  };

  const handleTileComplete = (doc) => {
    // P0 Stage-14b — optimistic post-upload prefetch.
    // The instant analysis completes, warm every cache the user is about to
    // touch on their way back to Reports / Home, so the next route renders
    // from cache with no skeleton. Background revalidation keeps it fresh.
    //
    // Three things happen in parallel:
    //   1. Splice the new doc into the cached list immediately (no refetch
    //      flash, no missing row).
    //   2. Prefetch /account/value-summary so Home's "IN YOUR RECORD" panel,
    //      the FirstUploadNudge auto-hide, and the lifecycle nudge logic all
    //      have fresh counters with zero perceived delay.
    //   3. Prefetch the document detail itself (`/documents/:id`) so tapping
    //      the report's tile from the success state is instant.
    //
    // All three are best-effort — if any fail, the existing /documents
    // refetch (refetchDocuments below) still pulls authoritative data.
    if (doc && doc.id) {
      // (1) Splice into the cached list.
      const current = Array.isArray(documentsData) ? documentsData : [];
      const idx = current.findIndex((d) => d && d.id === doc.id);
      const next = idx >= 0 ? [...current.slice(0, idx), doc, ...current.slice(idx + 1)] : [doc, ...current];
      warmSWRCache('/documents', next);

      // (2) Prefetch value-summary (background, deduplicated by useSWRCache).
      axios.get(`${API}/account/value-summary`)
        .then(({ data }) => warmSWRCache('/account/value-summary', data))
        .catch(() => { /* best effort */ });

      // (3) Prefetch the doc detail.
      axios.get(`${API}/documents/${doc.id}`)
        .then(({ data }) => warmSWRCache(`/documents/${doc.id}`, data))
        .catch(() => { /* best effort */ });
    }
    // (4) Authoritative refetch in the background — keeps everything correct.
    refetchDocuments();
  };

  const handleTileCancel = (tileId) => {
    setUploadQueue((q) => q.filter((t) => t.id !== tileId));
    markUploadClosed();
  };

  const filteredDocs = selectedCategory === 'all'
    ? documents
    : documents.filter((d) => d.category === selectedCategory);

  const totalDocs = documents.length;
  const hasAnyDocs = totalDocs > 0;

  return (
    <MainLayout desktopWide>
      {/* Mobile: existing single column. Desktop (lg+): 2-pane split with
          uploader + library on the left, contextual help/preview on the right. */}
      <div className="px-4 sm:px-6 lg:px-10 py-4 lg:py-10 lg:max-w-[1440px] lg:mx-auto" data-testid="documents-page">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12 lg:items-start">

          {/* LEFT (mobile: full; desktop: 7/12) — uploader + library */}
          <div className="lg:col-span-7 space-y-6 max-w-2xl mx-auto lg:mx-0 lg:max-w-none">

        {/* Header — editorial eyebrow + calm title */}
        <header className="pt-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Reports
          </p>
          <h1
            className="mt-1.5 text-[26px] sm:text-[30px] font-semibold text-[#0E1C2E] leading-tight tracking-tight"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
            data-testid="documents-title"
          >
            Your reports, <em className="italic font-medium">read and remembered.</em>
          </h1>
          <p className="mt-2 text-[13.5px] text-slate-500 leading-relaxed">
            Upload a report. Kindra explains it, saves the key points, and uses it in future answers.
          </p>
        </header>

        {/* Trust strip — always visible, high-impact micro-signal */}
        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-slate-500"
          data-testid="documents-trust-strip"
        >
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={13} weight="duotone" className="text-[#2FB3A0]" />
            Encrypted storage
          </span>
          <span className="hidden sm:inline text-slate-300">·</span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle size={13} weight="duotone" className="text-[#2FB3A0]" />
            Yours to delete anytime
          </span>
          <span className="hidden sm:inline text-slate-300">·</span>
          <span className="inline-flex items-center gap-1.5">
            <Clock size={13} weight="duotone" className="text-[#2FB3A0]" />
            Ready in under a minute
          </span>
        </div>

        {/* Upload dropzone — compact variant once the library has content */}
        <UploadDropzone
          onFiles={handleFiles}
          compact={hasAnyDocs}
        />

        {/* In-flight upload tiles */}
        <AnimatePresence>
          {uploadQueue.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="space-y-2"
              data-testid="upload-queue"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 px-0.5">
                Reading now · {uploadQueue.length}
              </p>
              {uploadQueue.map((tile) => (
                <UploadProgressTile
                  key={tile.id}
                  tileId={tile.id}
                  file={tile.file}
                  category={tile.category}
                  onComplete={handleTileComplete}
                  onCancel={handleTileCancel}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Library */}
        {hasAnyDocs && (
          <section className="space-y-3">
            <div className="flex items-end justify-between gap-3 px-0.5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Your library
                </p>
                <p className="text-[13.5px] text-[#0E1C2E] mt-0.5">
                  {totalDocs} {totalDocs === 1 ? 'document' : 'documents'} on file
                </p>
              </div>
            </div>

            {/* Filter pills — subtle, horizontal scroll on mobile */}
            <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto">
              <div className="flex gap-1.5 min-w-max" data-testid="documents-filters">
                {categories.map(({ key, label }) => {
                  const isActive = selectedCategory === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedCategory(key)}
                      className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-medium transition-colors ${
                        isActive
                          ? 'bg-[#0E1C2E] text-white'
                          : 'bg-white border border-[#ECE8E1] text-slate-600 hover:border-[#CFC8BB] hover:bg-[#FBFAF6]'
                      }`}
                      data-testid={`documents-filter-${key}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Document list */}
            {loading ? (
              <div className="flex justify-center py-8">
                <Spinner size={22} className="animate-spin text-[#0E1C2E]" />
              </div>
            ) : filteredDocs.length > 0 ? (
              <div className="space-y-2">
                {filteredDocs.map((doc) => (
                  <DocumentRow key={doc.id} doc={doc} onOpen={() => navigate(`/documents/${doc.id}`)} />
                ))}
              </div>
            ) : (
              <div className="text-center py-10 border border-dashed border-[#ECE8E1] rounded-xl bg-[#FBFAF6]/60">
                <p className="text-[13px] text-slate-500">
                  Nothing in <span className="font-medium text-[#0E1C2E]">{categories.find(c => c.key === selectedCategory)?.label}</span> yet.
                </p>
              </div>
            )}
          </section>
        )}

        {/* Empty state — no docs at all.
            Stage 20B+ Wave B — upgraded from a single italic line to a
            premium editorial empty state with example file-type chips so
            first-time users know exactly what Kindra reads best. */}
        {!loading && !hasAnyDocs && uploadQueue.length === 0 && (
          <div
            className="text-center pt-4 pb-10"
            data-testid="documents-empty"
          >
            <div
              aria-hidden
              className="inline-flex items-center justify-center w-11 h-11 rounded-full mb-4"
              style={{ backgroundColor: 'rgba(47,179,160,0.10)', border: '1px solid rgba(47,179,160,0.25)' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2FB3A0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <p
              className="font-serif text-[19px] leading-[1.3] tracking-[-0.005em] text-[#0E1C2E]"
            >
              Nothing here yet — and that's fine.
            </p>
            <p className="text-[13.5px] text-slate-500 leading-relaxed mt-2 max-w-md mx-auto">
              Once you add one report, Kindra will have real context for every chat, appointment, and pattern it spots. Drop a file up top — we'll take it from there.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-1.5 max-w-md mx-auto">
              {['Blood test', 'Imaging report', 'GP letter', 'Specialist notes'].map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] text-slate-600"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(14,28,46,0.08)' }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}
          </div>{/* /LEFT */}

          {/* RIGHT (lg+) — what-uploads-do panel.
              Editorial guidance — what happens when you drop a file, what
              kinds of files Kindra reads best, and a small confidence ladder. */}
          <aside className="hidden lg:block lg:col-span-5 lg:sticky lg:top-10 space-y-6">
            <div className="rounded-2xl border border-[rgba(14,28,46,0.10)] bg-white p-7">
              <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium mb-3">
                What Kindra does with it
              </p>
              <ol className="space-y-3 text-[13.5px] text-slate-600 leading-relaxed">
                <li className="flex gap-3">
                  <span className="font-serif italic text-[18px] text-[#235347] leading-none w-5 flex-shrink-0">01</span>
                  <span><strong className="text-[#0E1C2E] font-medium">Reads the whole report</strong> &mdash; tables, ranges, flagged values, doctor notes.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-serif italic text-[18px] text-[#235347] leading-none w-5 flex-shrink-0">02</span>
                  <span><strong className="text-[#0E1C2E] font-medium">Translates it into plain English</strong> &mdash; what's normal, what's worth a question, what doesn't matter.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-serif italic text-[18px] text-[#235347] leading-none w-5 flex-shrink-0">03</span>
                  <span><strong className="text-[#0E1C2E] font-medium">Saves it to your timeline</strong> &mdash; so the next chat already knows your TSH, your ferritin, your last A1c.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-serif italic text-[18px] text-[#235347] leading-none w-5 flex-shrink-0">04</span>
                  <span><strong className="text-[#0E1C2E] font-medium">Compares it against itself</strong> &mdash; once you upload a second of the same kind, Kindra spots what changed.</span>
                </li>
              </ol>
            </div>

            <div className="rounded-2xl border border-[rgba(14,28,46,0.10)] bg-[#FAF7EF] p-7">
              <p className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-medium mb-3">Reads best</p>
              <p className="text-[13.5px] text-slate-700 leading-relaxed">
                Pathology results · Imaging reports · GP letters · Referral letters · Specialist summaries · Discharge summaries · Hospital reports · Scripts.
              </p>
              <p className="mt-3 text-[12px] text-slate-500 leading-relaxed">
                File types: PDF · JPG · PNG · HEIC. Up to 25 MB each.
              </p>
            </div>

            <div className="rounded-2xl border border-[rgba(14,28,46,0.10)] bg-white p-7">
              <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium mb-2">Privacy</p>
              <p className="text-[13.5px] text-slate-600 leading-relaxed">
                Your reports are encrypted at rest, never used to train AI models, and yours to delete at any time. Kindra is a clinical companion &mdash; not a clinic, not a marketplace.
              </p>
            </div>
          </aside>

        </div>{/* /lg:grid */}
      </div>
    </MainLayout>
  );
};

/* ─── Row ───────────────────────────────────────────── */
const DocumentRow = ({ doc, onOpen }) => {
  const status = doc.extraction_status;
  const ready = status === 'completed' || status === 'complete';
  const analyzing = status === 'analyzing';
  const needsReview = status === 'needs_review';
  const failed = status === 'failed';

  const fileIcon = () => {
    if ((doc.file_type || '').includes('pdf')) {
      return <FilePdf size={20} className="text-red-500" weight="duotone" />;
    }
    if ((doc.file_type || '').includes('image')) {
      return <ImageIcon size={20} className="text-blue-500" weight="duotone" />;
    }
    return <FileText size={20} className="text-slate-500" weight="duotone" />;
  };

  const statusChip = () => {
    if (analyzing) {
      return (
        <span className="inline-flex items-center gap-1 text-[10.5px] text-[#2FB3A0] font-medium">
          <Spinner size={10} className="animate-spin" /> Reading
        </span>
      );
    }
    if (needsReview) {
      return <span className="text-[10.5px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded font-medium">Needs review</span>;
    }
    if (failed) {
      return (
        <span className="inline-flex items-center gap-1 text-[10.5px] text-red-600 font-medium">
          <Warning size={10} weight="fill" /> Retry
        </span>
      );
    }
    if (ready) {
      return <CheckCircle size={13} className="text-emerald-500" weight="fill" />;
    }
    return null;
  };

  return (
    <button
      onClick={onOpen}
      className="w-full bg-white border border-[#ECE8E1] rounded-xl p-3.5 text-left hover:border-[#CFC8BB] hover:bg-[#FBFAF6] transition-colors flex items-center gap-3.5"
      data-testid="document-row"
    >
      <div className="w-11 h-11 bg-[#F4F1EB] rounded-lg flex items-center justify-center flex-shrink-0">
        {fileIcon()}
      </div>
      <div className="flex-1 min-w-0">
        {/* Stage-14 — smart label first, raw filename quiet beneath. */}
        <h3 className="text-[14px] font-medium text-[#0E1C2E] truncate leading-tight" data-testid="document-row-label">
          {smartLabelFor(doc)}
        </h3>
        {smartLabelFor(doc) !== doc.filename && (
          <p className="text-[11px] text-slate-400 truncate leading-tight mt-0.5" title={doc.filename}>
            {doc.filename}
          </p>
        )}
        <div className="flex items-center gap-1.5 mt-1 text-[11px] text-slate-500">
          <span className="capitalize">{doc.category || 'document'}</span>
          <span className="text-slate-300">·</span>
          <span>{new Date(doc.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
          {statusChip() && (
            <>
              <span className="text-slate-300">·</span>
              {statusChip()}
            </>
          )}
        </div>
      </div>
      <CaretRight size={16} className="text-slate-300 flex-shrink-0" />
    </button>
  );
};

/** Light-touch category guess from filename — just a reasonable default. */
function guessCategory(name = '') {
  const n = name.toLowerCase();
  if (/(blood|pathology|iron|tsh|lipid|fbc|lab)/.test(n)) return 'pathology';
  if (/(mri|ct|ultrasound|xray|x-ray|scan|echo|imaging)/.test(n)) return 'imaging';
  if (/(letter|referral|specialist)/.test(n)) return 'letter';
  if (/(script|prescription|rx)/.test(n)) return 'prescription';
  if (/(report|summary|discharge)/.test(n)) return 'report';
  return 'other';
}

/** Stage-14 — smart human-readable label for a document. Prefers the
 * AI-extracted title, else maps the file to a category-based label, else
 * falls back to the raw filename. The raw filename remains visible quietly
 * underneath in the row so the user can verify which file this is.
 */
function smartLabelFor(doc) {
  if (!doc) return '';
  // 1. AI-extracted title from the analysis pipeline.
  const ai = (doc.smart_label || doc.ai_title || doc.summary_title || '').trim();
  if (ai) return ai;
  // 2. Category-based label.
  const cat = doc.category || guessCategory(doc.filename || '');
  const labels = {
    pathology:    'Pathology report',
    imaging:      'Imaging report',
    letter:       'GP / specialist letter',
    prescription: 'Prescription',
    report:       'Medical report',
    other:        'Document',
  };
  const base = labels[cat] || 'Document';
  // 3. Append a relative date so the row reads "Pathology report · 29 Apr".
  if (doc.created_at) {
    try {
      const d = new Date(doc.created_at);
      const dd = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
      return `${base} · ${dd}`;
    } catch { /* fall through */ }
  }
  return base;
}

export default DocumentsPage;
